import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Select, Space } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { formats } from '../../../library/constants/formats.js';
import dayjs from '../../../library/utils/dayjs';
import InfoLabel from '../../data/info-label.jsx';

// Filter drawer form shared by the quotation and production lists.
// Emits { start_date, end_date, status ("1,3"), product_name } (backend filter keys).
// The date range is two single-month pickers: a two-month range panel does not
// fit the drawer on narrow screens.
// The product list comes from the backend and holds only this vendor's products.
const ListFilterForm = ({ data, dateLabel, dateInfo, statusOptions, loadProducts, onFinish, ...otherProps }) => {
    const [form] = Form.useForm()
    const [products, setProducts] = useState([])
    const [productsLoading, setProductsLoading] = useState(true)
    const startDate = Form.useWatch('start_date', form)
    const endDate = Form.useWatch('end_date', form)

    useEffect(() => {
        let active = true
        loadProducts()
            .then((names) => { if (active) setProducts(names.map((name) => ({ value: name, label: name }))) })
            .catch(() => { if (active) setProducts([]) })
            .finally(() => { if (active) setProductsLoading(false) })
        return () => { active = false }
    }, [loadProducts])

    const initialValues = useMemo(() => {
        const toDay = (value) => (value ? dayjs(value, formats.backendDateFormat) : null)
        const start = toDay(data?.start_date)
        const end = toDay(data?.end_date)
        const status = String(data?.status ?? '').split(',').map(Number).filter(Number.isInteger).filter(Boolean)
        return {
            start_date: start,
            end_date: end,
            status: status.length ? status : [],
            product_name: data?.product_name ?? null,
        }
    }, [data])

    const handleFinish = ({ start_date, end_date, status, product_name }) => {
        onFinish({
            start_date: start_date?.format(formats.backendDateFormat) ?? null,
            end_date: end_date?.format(formats.backendDateFormat) ?? null,
            status: status?.length ? status.join(',') : null,
            product_name: product_name ?? null,
        })
    }

    const clearForm = () => {
        form.setFieldsValue({ start_date: null, end_date: null, status: [], product_name: null })
        form.submit()
    }

    return (
        <Form initialValues={initialValues} form={form} layout='vertical' onFinish={handleFinish} {...otherProps}>
            <Form.Item label={dateInfo ? <InfoLabel label={dateLabel} info={dateInfo} /> : dateLabel} style={{ marginBottom: 0 }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="start_date" style={{ width: '50%' }}>
                        <DatePicker
                            placeholder="From"
                            style={{ width: '100%' }}
                            format={formats.frontendDateFormat}
                            disabledDate={(day) => !!endDate && day.isAfter(endDate, 'day')}
                            getPopupContainer={() => document.body}
                        />
                    </Form.Item>
                    <Form.Item name="end_date" style={{ width: '50%' }}>
                        <DatePicker
                            placeholder="To"
                            style={{ width: '100%' }}
                            format={formats.frontendDateFormat}
                            disabledDate={(day) => !!startDate && day.isBefore(startDate, 'day')}
                            getPopupContainer={() => document.body}
                        />
                    </Form.Item>
                </Space.Compact>
            </Form.Item>
            <Form.Item name="status" label="Status">
                <Select
                    mode="multiple"
                    placeholder="Any status"
                    options={statusOptions}
                    allowClear
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                />
            </Form.Item>
            <Form.Item name="product_name" label="Product Name">
                <Select
                    placeholder="Search your products"
                    options={products}
                    loading={productsLoading}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    notFoundContent={productsLoading ? 'Loading…' : 'No products'}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                />
            </Form.Item>
            <div className="actions filter-actions">
                <Button htmlType="submit" type="primary" icon={<FilterOutlined />}>Apply</Button>
                <Button onClick={clearForm} icon={<ReloadOutlined />}>Reset</Button>
            </div>
        </Form>
    )
}

ListFilterForm.propTypes = {
    data: PropTypes.object,
    dateLabel: PropTypes.string.isRequired,
    dateInfo: PropTypes.node,
    statusOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })).isRequired,
    loadProducts: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
}

export default ListFilterForm
